import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE, API_PREFIX, authFetch, url } from './apiClient';
import { forMonth, monthLabel, occupancy, percent, profitFor, rupees, summarize, unpaidList } from './dashboardStats';
import { formatDate } from './dateFormat';
import Icon from './Icons';
import './Dashboard.css';

const BASE = `${API_BASE}${API_PREFIX}/tenants`;

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// Each side panel loads on its own: a failing complaints call must not blank the money figures.
async function getJson(href) {
  const res = await authFetch(href);
  if (!res.ok) throw new Error('Request failed');
  return res.json();
}

function Kpi({ icon, tone, label, value, sub, to }) {
  const body = (
    <>
      <div className={`kpi__icon kpi__icon--${tone}`}><Icon name={icon} size={22} /></div>
      <div className="kpi__value">{value}</div>
      <div className="kpi__label">{label}</div>
      {sub && <div className="kpi__sub">{sub}</div>}
    </>
  );
  return to ? <Link to={to} className="kpi kpi--link">{body}</Link> : <div className="kpi">{body}</div>;
}

function Donut({ paid, total, size = 168, stroke = 18 }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = percent(paid, total);
  return (
    <div className="donut" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`${pct}% collected`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--primary-tint)" strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke="url(#donutGrad)" strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={`${(c * pct) / 100} ${c}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dasharray 0.6s ease' }}
        />
        <defs>
          <linearGradient id="donutGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#15803d" />
            <stop offset="100%" stopColor="#4ade80" />
          </linearGradient>
        </defs>
      </svg>
      <div className="donut__center">
        <div className="donut__pct">{pct}%</div>
        <div className="donut__cap">collected</div>
      </div>
    </div>
  );
}

function ProfitCard({ rentCollected, spent, profit }) {
  return (
    <Link to="/admin/expenses" className="typecard typecard--link">
      <div className="typecard__head">
        <span className="chip chip--green"><Icon name="rupee" size={14} /> Profit</span>
        <span className="typecard__count">Rent − expenses</span>
      </div>
      <div className="typecard__amounts">
        <strong className={profit < 0 ? 'text-danger' : ''}>{rupees(profit)}</strong>
      </div>
      <div className="typecard__foot">
        <span>Rent collected {rupees(rentCollected)}</span>
        <span>Expenses {rupees(spent)}</span>
      </div>
    </Link>
  );
}

function TypeProgress({ title, icon, tone, group }) {
  const pct = percent(group.collected, group.raised);
  return (
    <div className="typecard">
      <div className="typecard__head">
        <span className={`chip chip--${tone}`}><Icon name={icon} size={14} /> {title}</span>
        <span className="typecard__count">{group.paidCount}/{group.count} paid</span>
      </div>
      <div className="typecard__amounts">
        <strong>{rupees(group.collected)}</strong>
        <span> of {rupees(group.raised)}</span>
      </div>
      <div className="bar"><div className={`bar__fill bar__fill--${tone}`} style={{ width: `${pct}%` }} /></div>
      <div className="typecard__foot">
        <span>{pct}% collected</span>
        <span className={group.pending > 0 ? 'text-danger' : ''}>{rupees(group.pending)} pending</span>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [bills, setBills] = useState([]);
  const [users, setUsers] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [vacates, setVacates] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [month, setMonth] = useState(currentMonth());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    const [b, u, c, v, x] = await Promise.allSettled([
      getJson(`${BASE}/all`),
      getJson(url.adminUsersAll()),
      getJson(`${BASE}/complaints`),
      getJson(url.adminVacateAll()),
      getJson(url.adminExpenses()),
    ]);
    if (b.status === 'fulfilled') setBills(Array.isArray(b.value) ? b.value : []);
    else setError('Could not load the bills');
    if (u.status === 'fulfilled' && Array.isArray(u.value)) setUsers(u.value);
    if (c.status === 'fulfilled' && Array.isArray(c.value)) setComplaints(c.value);
    if (v.status === 'fulfilled' && Array.isArray(v.value)) setVacates(v.value);
    if (x.status === 'fulfilled' && Array.isArray(x.value)) setExpenses(x.value);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const months = useMemo(() => Array.from(new Set(bills.map((b) => b.monthYear))).sort().reverse(), [bills]);
  const scoped = useMemo(() => forMonth(bills, month), [bills, month]);
  const stats = useMemo(() => summarize(scoped), [scoped]);
  const unpaid = useMemo(() => unpaidList(scoped), [scoped]);
  const money = useMemo(() => profitFor(stats.rent.collected, expenses, month), [stats, expenses, month]);
  const rooms = useMemo(() => occupancy(users), [users]);
  const names = useMemo(() => Object.fromEntries(users.map((u) => [u.username, u.fullName])), [users]);
  const openComplaints = useMemo(
    () => complaints.filter((c) => c.status === 'OPEN').sort((a, b) => String(b.createdDate).localeCompare(String(a.createdDate))),
    [complaints]);
  const pendingVacates = vacates.filter((v) => v.status === 'PENDING');
  const period = month ? monthLabel(month) : 'all months';

  return (
    <div className="dash">
      <div className="dash-head">
        <div>
          <h2 className="dash-title">Dashboard</h2>
          <p className="dash-sub">Overview for <strong>{period}</strong></p>
        </div>
        <div className="dash-actions">
          <label className="field-inline">
            <span>Month</span>
            <select id="dash-month" value={month} onChange={(e) => setMonth(e.target.value)}>
              <option value="">All months</option>
              {[...new Set([currentMonth(), ...months])].sort().reverse().map((m) => (
                <option key={m} value={m}>{monthLabel(m)}</option>
              ))}
            </select>
          </label>
          <button type="button" className="btn btn--ghost" onClick={load} disabled={loading}>
            <Icon name="refresh" size={16} /> {loading ? 'Refreshing…' : 'Refresh'}
          </button>
          <Link to="/admin/add-bill" className="btn btn--primary"><Icon name="plus" size={16} /> New bill</Link>
        </div>
      </div>

      {error && <div className="alert alert--danger">{error}</div>}

      <div className="kpi-grid">
        <Kpi icon="bed" tone="brown" label="Rooms" value={loading ? '—' : rooms.rooms}
          sub={`${rooms.registered} registered · ${rooms.notRegistered} not yet`} to="/admin/users" />
        <Kpi icon="check" tone="green" label="Collected" value={loading ? '—' : rupees(stats.all.collected)}
          sub={`${percent(stats.all.collected, stats.all.raised)}% of ${rupees(stats.all.raised)}`} to="/admin/paid-report" />
        <Kpi icon="rupee" tone="red" label="Pending" value={loading ? '—' : rupees(stats.all.pending)}
          sub={`${stats.all.unpaidCount} unpaid bill${stats.all.unpaidCount === 1 ? '' : 's'}`} to="/admin/view-bills" />
        <Kpi icon="wrench" tone="amber" label="Open complaints" value={loading ? '—' : openComplaints.length}
          sub={openComplaints.length ? 'Needs your attention' : 'All resolved'} to="/admin/complaints" />
        <Kpi icon="door" tone="blue" label="Vacate requests" value={loading ? '—' : pendingVacates.length}
          sub={pendingVacates.length ? 'Waiting for approval' : 'None waiting'} to="/admin/vacate" />
        <Kpi icon="shield" tone="brown" label="Deposits held" value={loading ? '—' : rupees(rooms.deposits)}
          sub="Across all tenants" />
      </div>

      {!loading && !error && stats.all.count === 0 ? (
        <div className="panel empty">
          <Icon name="receipt" size={36} />
          <h3>No bills for {period} yet</h3>
          <p>Raise this month's bills to start tracking collection.</p>
          <div className="empty__actions">
            <Link to="/admin/add-bill" className="btn btn--primary">Add a bill</Link>
            <Link to="/admin/bulk-bills" className="btn btn--ghost">Upload bulk bills</Link>
          </div>
        </div>
      ) : (
        <>
          <div className="dash-row dash-row--collect">
            <div className="panel collect">
              <div className="panel__head"><h3>Collection</h3><span className="muted">{period}</span></div>
              <div className="collect__body">
                <Donut paid={stats.all.collected} total={stats.all.raised} />
                <ul className="legend">
                  <li><span className="dot dot--green" />Collected<strong>{rupees(stats.all.collected)}</strong></li>
                  <li><span className="dot dot--tint" />Pending<strong>{rupees(stats.all.pending)}</strong></li>
                  <li className="legend__total">Total raised<strong>{rupees(stats.all.raised)}</strong></li>
                </ul>
              </div>
            </div>
            <div className="typecards">
              <TypeProgress title="Rent" icon="home" tone="brown" group={stats.rent} />
              <TypeProgress title="Electricity" icon="bolt" tone="amber" group={stats.electricity} />
              <ProfitCard rentCollected={stats.rent.collected} spent={money.spent} profit={money.profit} />
            </div>
          </div>

          <div className="dash-row dash-row--lists">
            <div className="panel">
              <div className="panel__head">
                <h3>Still to collect <span className="count">{unpaid.length}</span></h3>
                <Link to="/admin/view-bills" className="link">View all bills <Icon name="arrow" size={14} /></Link>
              </div>
              {unpaid.length === 0 ? (
                <div className="allclear"><Icon name="check" size={28} /> Everything for {period} has been collected.</div>
              ) : (
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr><th>Tenant</th>{!month && <th>Month</th>}<th>Bill</th><th className="num">Amount</th></tr>
                    </thead>
                    <tbody>
                      {unpaid.map((u) => (
                        <tr key={`${u.tenant}-${u.month}-${u.type}`}>
                          <td>
                            <div className="who">
                              <span className="who__avatar">{String(u.tenant).replace(/\D/g, '') || '?'}</span>
                              <span>
                                <span className="who__name">{names[u.tenant] || u.tenant}</span>
                                {names[u.tenant] && <span className="who__room">{u.tenant}</span>}
                              </span>
                            </div>
                          </td>
                          {!month && <td>{monthLabel(u.month)}</td>}
                          <td>
                            <span className={`chip chip--${u.type === 'electricity' ? 'amber' : 'brown'}`}>
                              {u.type === 'electricity' ? 'Electricity' : 'Rent'}
                            </span>
                          </td>
                          <td className="num"><strong>{rupees(u.amount)}</strong></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="panel">
              <div className="panel__head">
                <h3>Needs attention</h3>
              </div>
              {openComplaints.length === 0 && pendingVacates.length === 0 ? (
                <div className="allclear"><Icon name="check" size={28} /> Nothing waiting on you.</div>
              ) : (
                <ul className="feed">
                  {pendingVacates.map((v) => (
                    <li key={`v${v.id}`}>
                      <Link to="/admin/vacate" className="feed__item">
                        <span className="feed__icon feed__icon--blue"><Icon name="door" size={16} /></span>
                        <span className="feed__text">
                          <strong>{names[v.tenantUsername] || v.tenantUsername}</strong> wants to vacate
                          <span className="feed__meta">Leaving {formatDate(v.vacateDate)}</span>
                        </span>
                      </Link>
                    </li>
                  ))}
                  {openComplaints.slice(0, 6).map((c) => (
                    <li key={`c${c.id}`}>
                      <Link to="/admin/complaints" className="feed__item">
                        <span className="feed__icon feed__icon--amber"><Icon name="wrench" size={16} /></span>
                        <span className="feed__text">
                          <strong>{names[c.tenantName] || c.tenantName}</strong>
                          <span className="feed__desc">{c.description}</span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
